import { useCallback, type ReactNode } from 'react'
import { DateTime, type WeekdayNumbers } from 'luxon'
import { TSection } from 'app/components/terros'
import { TButton } from 'app/components/buttons'
import { type Minutes, type Weekday } from '@terros/common'
import { Group } from '@mantine/core'
import { usePreferenceContext } from '../Context'
import { generateTimeSlots, weekDays, type TimeslotFormValues } from '@front/model'
import { type UseFormReturnType } from '@mantine/form'
import { TimeslotButton } from './TimeslotButton'

type WithTimeslotsForm = {
  form: UseFormReturnType<TimeslotFormValues>
}

export const TimeslotsList = ({ form }: WithTimeslotsForm): ReactNode => {
  return weekDays.map((weekday) => <WeekdaySection key={weekday} weekday={weekday} form={form} />)
}

const WeekdaySection = ({ weekday, form }: { weekday: Weekday } & WithTimeslotsForm): ReactNode => {
  const { isAllowedUpdate } = usePreferenceContext()
  const { granularity, meetingDuration, timeslots } = form.values

  const availableTimes = generateTimeSlots(7, 21, granularity)
  const weekdayName = DateTime.fromObject({ weekday: WEEKDAY_MAP[weekday] }).toFormat('cccc')
  const allSelected = isAllSelected(availableTimes, timeslots[weekday])

  const handleToggleWeekday = useCallback((): void => {
    if (!isAllowedUpdate) return
    form.setFieldValue('timeslots', (prev) => {
      if (isAllSelected(availableTimes, prev[weekday])) {
        return { ...prev, [weekday]: [] }
      } else {
        return { ...prev, [weekday]: availableTimes }
      }
    })
  }, [form, availableTimes, weekday, isAllowedUpdate])

  const handleToggleSlot = useCallback(
    (time: Minutes): void => {
      if (!isAllowedUpdate) return
      const dayTimes = timeslots[weekday]
      const updatedTimes = dayTimes.includes(time)
        ? dayTimes.filter((t) => t !== time)
        : [...dayTimes, time].sort((a, b) => a - b)
      form.setFieldValue(`timeslots.${weekday}`, updatedTimes)
    },
    [form, timeslots, weekday, isAllowedUpdate]
  )

  return (
    <TSection
      key={weekday}
      heading={{
        title: weekdayName,
        children: isAllowedUpdate && (
          <TButton variant='secondary' onClick={handleToggleWeekday}>
            {allSelected ? 'Deselect All' : 'Select All'}
          </TButton>
        ),
      }}
    >
      <Group gap='xs' wrap='wrap'>
        {availableTimes.map((time) => (
          <TimeslotButton
            key={time}
            startMinutes={time}
            duration={meetingDuration}
            isSelected={timeslots[weekday].includes(time)}
            onToggle={() => handleToggleSlot(time)}
          />
        ))}
      </Group>
    </TSection>
  )
}

const WEEKDAY_MAP: Record<Weekday, WeekdayNumbers> = {
  sun: 7,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
}

const isAllSelected = (available: Minutes[], selected: Minutes[]): boolean => {
  return available.length === selected.length && selected.every((time) => available.includes(time))
}
